import { useEffect, useRef } from 'react';

/**
 * useScrollReveal Hook
 * 
 * Implements smooth section reveal based on scroll direction.
 */
const useScrollReveal = (selector = '.section, section', trigger) => {
    const lastScrollY = useRef(0);
    const scrollDirection = useRef('down');
    const observer = useRef(null);

    useEffect(() => {
        // 1. Handle Scroll Direction with Throttling
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > lastScrollY.current) {
                scrollDirection.current = 'down';
            } else if (currentScrollY < lastScrollY.current) {
                scrollDirection.current = 'up';
            }
            lastScrollY.current = currentScrollY;
        };

        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        // 2. Intersection Observer Logic
        const options = {
            root: null,
            rootMargin: '-10% 0px -10% 0px',
            threshold: [0, 0.1, 0.2]
        };

        const handleIntersect = (entries) => {
            entries.forEach(entry => {
                const { target, isIntersecting } = entry;

                if (isIntersecting) {
                    target.classList.add('is-active');
                    target.classList.remove('is-exiting');
                } else {
                    target.classList.remove('is-active');
                    target.classList.add('is-exiting');
                }
            });
        };

        observer.current = new IntersectionObserver(handleIntersect, options);

        // 3. Observe Targets - Slight delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const targets = document.querySelectorAll(selector);
            targets.forEach(target => {
                target.classList.add('reveal-section');
                observer.current.observe(target);
            });
        }, 100);

        return () => {
            window.removeEventListener('scroll', onScroll);
            if (observer.current) {
                observer.current.disconnect();
            }
            clearTimeout(timer);
        };
    }, [selector, trigger]);
};

export default useScrollReveal;
